import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  FUNCTION_RESULT_STATUS,
  ICustomer,
  IObjectAddress,
} from "@nova/common-interfaces";
import { BlockUIModule } from "ng-block-ui";
import {
  ConfirmationService,
  MessageService,
  ToastMessageOptions,
} from "primeng/api";
import { AutoCompleteModule } from "primeng/autocomplete";
import { ButtonModule } from "primeng/button";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { InputMaskModule } from "primeng/inputmask";
import { InputNumberModule } from "primeng/inputnumber";
import { InputTextModule } from "primeng/inputtext";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TextareaModule } from "primeng/textarea";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import * as SmartyStreetsSDK from "smartystreets-javascript-sdk";
import Stripe from "stripe";
import { PlatformUserService } from "../../../../../lib/services/platform/users/platform-user.service.mjs";
import { ThirdpartyStripeCheckoutFormComponent } from "../../../../billing/components/third-party/stripe/stripe-payment/stripe-payment.component";
import { CustomerService } from "../../../../shared/services/customers/customers.service.mjs";
@Component({
  selector: "app-customer-edit",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    ToggleSwitchModule,
    BlockUIModule,
    AutoCompleteModule,
    TextareaModule,
    InputMaskModule,
    ButtonModule,
    ProgressSpinnerModule,
    InputNumberModule,
  ],
  providers: [MessageService, ConfirmationService, DialogService],
  templateUrl: "./customer-edit.component.html",
  styleUrls: ["./customer-edit.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CustomerEditComponent implements OnInit {
  customerForm!: FormGroup;
  isLoading = true;
  customerId!: string;
  customer: ICustomer | undefined;
  addressOptions = <any>[];
  passwordResetLink: string | undefined;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private platformUserService: PlatformUserService,
    private dialogService: DialogService,
    private router: Router,
    private customerService: CustomerService
  ) {}

  resetPasswordLink() {
    this.platformUserService.resetPasswordLink(this.customer!.email).then(result => {
      console.log(result);
      if (result.status === FUNCTION_RESULT_STATUS.SUCCESS)
      {
        this.passwordResetLink = result.data!;
        console.log(this.passwordResetLink);
      }
    })
  }
  addressSelected(event: any) {
    console.log("Search result", event.value);
    this.customerForm.controls["line1"].setValue(event.value.street_line);
    this.customerForm.controls["town"].setValue(event.value.city);
    this.customerForm.controls["state"].setValue(event.value.state);
    this.customerForm.controls["postal_code"].setValue(event.value.zipcode);
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.customerForm = this.fb.group({
      first_name: ["", Validators.required],
      last_name: ["", Validators.required],
      email: ["", Validators.required],
      mobile_phone: ["", Validators.required],
      line1: ["", Validators.required],
      autopay_status: [false, Validators.required],
      town: ["", Validators.required],
      state: ["", Validators.required],
      postal_code: ["", Validators.required],
    });

    const id = this.route.snapshot.params["id"];

    this.customerService.get(id).then((result) => {
      if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
        return Promise.reject("Unable to find customer");
      }

      this.customer = result.data;

      console.log("Customer", this.customer);

      this.customerForm.controls["first_name"].setValue(
        this.customer?.first_name
      );
      this.customerForm.controls["autopay_status"].setValue(
        this.customer?.autopay_status
      );

      this.customerForm.controls["last_name"].setValue(
        this.customer?.last_name
      );
      this.customerForm.controls["email"].setValue(this.customer?.email);
      this.customerForm.controls["mobile_phone"].setValue(
        this.customer?.mobile_phone
      );
      this.customerForm.controls["line1"].setValue(
        this.customer?.mailing_address?.line1
      );
      this.customerForm.controls["town"].setValue(
        this.customer?.mailing_address?.town
      );
      this.customerForm.controls["state"].setValue(
        this.customer?.mailing_address?.state
      );
      this.customerForm.controls["postal_code"].setValue(
        this.customer?.mailing_address?.postal_code
      );

      return Promise.resolve();
    });

    this.isLoading = false;
  }

  ref: DynamicDialogRef | undefined;

  cancel() {
    this.router.navigate(["/admin/customers/view", this.customer?.id]);
  }

  addPaymentMethod() {
    this.ref = this.dialogService.open(ThirdpartyStripeCheckoutFormComponent, {
      data: {
        email: this.customer?.email,
      },
      width: "50vw",
      modal: true,
      focusOnShow: false,
      style: {
        "background-color": "#111827",
      },
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
    });

    this.ref.onClose.subscribe((data: Stripe.PaymentIntent) => {
      if (data) {
        this.messageService.add({
          severity: "success",
          text: "Payment was successful. Thank you!",
          life: 3000,
        } as ToastMessageOptions);
      }
    });
  }

  async searchAddress(event: any) {
    console.log(event);
    const SmartyStreetsCore = SmartyStreetsSDK.core;
    const websiteKey = "112622355404515731"; // Your Website Key
    const Lookup = SmartyStreetsSDK.usAutocompletePro.Lookup;
    const credentials = new SmartyStreetsCore.SharedCredentials(websiteKey);
    const clientBuilder = new SmartyStreetsCore.ClientBuilder(credentials);
    const client = clientBuilder.buildUsAutocompleteProClient();
    const lookup = new Lookup(event.query);
    lookup.maxResults = 10;
    const result = await client.send(lookup);
    console.log("Smarty", result);
    this.addressOptions = result.result as any[];
  }

  async onSubmit(): Promise<void> {
    if (this.customerForm.valid) {
      this.isLoading = true;

      const updateCustomer: ICustomer = { ...this.customerForm.value };
      updateCustomer.id = this.customer!.id;
      updateCustomer.total_balance = this.customer!.total_balance;
      updateCustomer.total_subscriptions = this.customer!.total_subscriptions;
      updateCustomer.status = this.customer?.status!;
      updateCustomer.mailing_address = {
        line1: this.customerForm.get("line1")?.value.streetLine,
        town: this.customerForm.get("town")?.value,
        state: this.customerForm.get("state")?.value,
        postal_code: this.customerForm.get("postal_code")?.value,
      } as IObjectAddress;

      const SmartyStreetsCore = SmartyStreetsSDK.core;
      const websiteKey = "112622355404515731"; // Your Website Key
      const Lookup = SmartyStreetsSDK.usStreet.Lookup;
      const credentials = new SmartyStreetsCore.SharedCredentials(websiteKey);
      const clientBuilder = new SmartyStreetsCore.ClientBuilder(credentials);
      const client = clientBuilder.buildUsStreetApiClient();
      const lookup = new Lookup(
        updateCustomer.mailing_address.line1,
        undefined,
        undefined,
        updateCustomer.mailing_address.town,
        updateCustomer.mailing_address.state,
        updateCustomer.mailing_address.postal_code
      );
      const result = await client.send(lookup);
      console.log("SMARTY STREET", result.lookups[0].result[0].metadata);
     const results = result.lookups as any[];
     if (results.length > 0)
     {
      updateCustomer.latitude = results[0].result[0].metadata.latitude;
      updateCustomer.longitude = results[0].result[0].metadata.longitude;
     }

      updateCustomer.latitude = this.customer?.latitude;
      updateCustomer.longitude = this.customer?.longitude;
      updateCustomer.activation_date = this.customer?.activation_date!;
      updateCustomer.mobile_phone =
        this.customerForm.get("mobile_phone")?.value;
      updateCustomer.home_home = this.customer?.home_home!;
      updateCustomer.name = `${updateCustomer.first_name} ${updateCustomer.last_name}`;
      updateCustomer.autopay_status = this.customer?.autopay_status!;
      updateCustomer.token_uid = this.customer?.token_uid!;
      updateCustomer.comments = this.customer?.comments;
      updateCustomer.reference_keys = this.customer?.reference_keys!;
      return this.customerService
        .update(updateCustomer)
        .then(() => {
          console.log("Customer create successfully");
          this.router.navigate(["/admin/customers/view", updateCustomer.id]);
        })
        .catch((error) => {
          console.error("Error creating customer:", error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
}
